import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import DiploksWalletSummaryCard from "../../components/DiploksWalletSummaryCard";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Users, CalendarRange, Award, ScrollText, ArrowUpRight,
  School as SchoolIcon, Receipt, GraduationCap,
} from "lucide-react";

export default function SchoolAdminDashboard() {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [delegates, setDelegates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (user?.school_id) {
      api.get(`/v2/schools?organization_id=${user.organization_id}`)
        .then((r) => {
          const s = r.data.items?.find((x) => x.id === user.school_id);
          setSchool(s);
        })
        .catch(() => {});
    }
    api.get(`/v2/delegates?limit=100${user?.school_id ? `&school_id=${user.school_id}` : ""}`)
      .then((r) => setDelegates(r.data.items || []))
      .catch(() => setDelegates([]));
    api.get("/v2/invoices?limit=20")
      .then((r) => setInvoices(r.data.items || []))
      .catch(() => setInvoices([]));
    api.get("/v2/events?limit=10")
      .then((r) => setEvents(r.data.items || []))
      .catch(() => setEvents([]));
  }, [user]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            School Console
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            {school?.name || "Your school"}
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Manage your delegation — roster, invoices, and event participation.
          </p>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile icon={Users} label="Delegates" value={delegates.length} />
          <Tile icon={CalendarRange} label="Events" value={events.length} />
          <Tile icon={Receipt} label="Invoices" value={invoices.length} />
          <Tile icon={Award} label="Certs" value="—" />
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">Roster</h2>
              <Link to="/dashboard/school/delegates">
                <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81]" data-testid="view-all-delegates-button">
                  View all <ArrowUpRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="mt-4 rounded-xl bg-white border border-slate-200 divide-y divide-slate-100">
              {delegates.slice(0, 6).map((d) => (
                <div key={d.id} className="flex items-center justify-between px-5 py-4" data-testid={`school-delegate-${d.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.email} · Grade {d.grade || "—"}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#0A192F]">
                    {d.experience_level || "beginner"}
                  </span>
                </div>
              ))}
              {delegates.length === 0 && (
                <p className="p-10 text-center text-slate-500 text-sm">No delegates yet.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <DiploksWalletSummaryCard />
            <Card className="bg-[#0A192F] text-white border-[#D4AF37]/30">
              <CardContent className="p-6">
                <SchoolIcon className="h-5 w-5 text-[#D4AF37]" />
                <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  School profile
                </p>
                <h3 className="mt-1 text-xl font-semibold tracking-tight">{school?.name || "—"}</h3>
                <p className="mt-2 text-sm text-slate-300">{school?.city}, {school?.country}</p>
                {school?.primary_contact_email && (
                  <p className="mt-3 text-xs text-slate-400 font-mono">{school.primary_contact_email}</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Quick actions
                </p>
                <div className="mt-4 space-y-2">
                  <Link to="/dashboard/school/invoices" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37]">
                    <span className="text-sm font-semibold text-[#0A192F]">View invoices</span>
                    <ScrollText className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                  <Link to="/dashboard/events" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37]">
                    <span className="text-sm font-semibold text-[#0A192F]">Browse events</span>
                    <CalendarRange className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                  <Link to="/dashboard/school/delegates" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37]">
                    <span className="text-sm font-semibold text-[#0A192F]">Manage delegates</span>
                    <GraduationCap className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1 font-serif text-3xl text-[#0A192F]">{value ?? "—"}</p>
    </div>
  );
}
